/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useTranslation } from 'react-i18next';

import { DetailTimeline, Text } from '@/shared/ui';
import { formatLocalizedDate } from '@/shared/utils/date';
import { formatStatsCurrency } from '@/shared/utils/format-stats';

import { PanelEmptyState, formatCalendarDate } from './sale-detail-atoms';
import { milestoneLabel } from '../constants/sale-states';
import type { Sale, SaleEvent, SalePaperworkMilestoneType } from '../types/sale.types';

export interface SaleActivityFeedProps {
  sale: Sale;
  /** The agency's configured milestone types, to resolve labels from. */
  milestoneTypes: SalePaperworkMilestoneType[];
}

interface FeedEntry {
  key: string;
  title: string;
  detail?: string;
  /** Raw date/timestamp, used only to sort — never rendered directly. */
  sortKey: string;
  /** Already formatted for display, or null when the record has no date. */
  displayDate: string | null;
  accent: string;
}

/**
 * What has actually happened to this sale, in reverse order.
 *
 * Assembled from facts the record holds: the paperwork dates, the
 * commission receipts, and — since [Plano 4, D9] — the record's own
 * `events`, which is the only thing that marks *when* a sale reached a
 * terminal state. A sale voided before that field existed falls back to
 * the undo reason with no date, same as before.
 *
 * @param props.sale - The sale record.
 * @returns The rendered activity column.
 */
export function SaleActivityFeed({ sale, milestoneTypes }: SaleActivityFeedProps) {
  const { t } = useTranslation('features/sales');
  const entries = buildEntries(sale, milestoneTypes, t);

  return (
    <DetailTimeline
      testId="sale-activity-feed"
      title={t('detail.activity.title')}
      items={entries.map((entry) => ({
        id: entry.key,
        testId: 'sale-activity-entry',
        title: entry.title,
        detail: entry.detail,
        meta: entry.displayDate ?? t('detail.activity.voidedNoDate'),
        accent: entry.accent,
        ringed: true,
      }))}
      empty={
        <PanelEmptyState
          message={t('detail.activity.empty')}
          hint={t('detail.activity.emptyHint')}
        />
      }
      footer={
        <Text component="span" variant="meta" tone="tertiary" className="leading-relaxed">
          {t('detail.activity.note')}
        </Text>
      }
    />
  );
}

type Translate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The dated paperwork milestones as feed entries.
 *
 * @param sale - The sale record.
 * @param milestoneTypes - The agency's configured milestone types.
 * @param t - The namespace translator.
 * @returns One entry per milestone that carries a date.
 */
function milestoneEntries(
  sale: Sale, milestoneTypes: SalePaperworkMilestoneType[], t: Translate,
): FeedEntry[] {
  return Object.entries(sale.paperwork)
    .filter(([, date]) => Boolean(date))
    .map(([id, date]) => {
      const type = milestoneTypes.find((m) => m.id === id);
      return {
        key: `milestone-${id}`,
        title: type ? milestoneLabel(type, t) : id,
        detail: t('detail.paperwork.recorded'),
        sortKey: date,
        displayDate: formatCalendarDate(date),
        accent: 'var(--accent-salvia)',
      };
    });
}

/**
 * The commission receipts as feed entries.
 *
 * @param sale - The sale record.
 * @param t - The namespace translator.
 * @returns One entry per received commission.
 */
function receiptEntries(sale: Sale, t: Translate): FeedEntry[] {
  return (sale.commission_receipts ?? [])
    .filter((r) => Boolean(r.received_at))
    .map((r, i) => ({
      key: `receipt-${r.received_at}-${i}`,
      title: t('detail.activity.receipt'),
      detail: [formatStatsCurrency(r.amount), r.recipient].filter(Boolean).join(' · '),
      sortKey: r.received_at,
      displayDate: formatCalendarDate(r.received_at),
      accent: 'var(--seed-base)',
    }));
}

/**
 * The reason to show for a state event: only voids carry one.
 *
 * @param sale - The sale the event belongs to.
 * @param e - The state event being rendered.
 * @returns The reason text, or undefined when the event carries none.
 */
function stateEventDetail(sale: Sale, e: SaleEvent): string | undefined {
  if (e.type !== 'voided') return undefined;
  return e.reason || sale.cancellation_reason || undefined;
}

/**
 * One terminal-state event (concluded/voided) as a feed entry.
 *
 * @param sale - The owning sale record, for the void fallback reason.
 * @param e - The state event.
 * @param i - The event's index, for a stable key.
 * @param t - The namespace translator.
 * @returns The rendered feed entry.
 */
function stateEventEntry(
  sale: Sale, e: SaleEvent, i: number, t: Translate,
): FeedEntry {
  const concluded = e.type === 'concluded';
  return {
    key: `event-${e.type}-${i}`,
    title: concluded ? t('detail.activity.concluded') : t('detail.activity.voided'),
    detail: stateEventDetail(sale, e),
    sortKey: e.timestamp,
    displayDate: formatLocalizedDate(e.timestamp),
    accent: concluded ? 'var(--accent-salvia)' : 'var(--accent-cobre)',
  };
}

/**
 * The dated terminal-state events (concluded/voided) as feed entries.
 *
 * @param sale - The sale record.
 * @param t - The namespace translator.
 * @returns One entry per recorded state event.
 */
function stateEventEntries(sale: Sale, t: Translate): FeedEntry[] {
  return (sale.events ?? []).map((e, i) => stateEventEntry(sale, e, i, t));
}

/**
 * The undated legacy-void entry, when the sale was voided before the
 * `events` field carried the fact.
 *
 * @param sale - The sale record.
 * @param t - The namespace translator.
 * @returns A single-entry array, or empty when no legacy void applies.
 */
function legacyVoidEntries(sale: Sale, t: Translate): FeedEntry[] {
  const hasVoidedEvent = (sale.events ?? []).some((e) => e.type === 'voided');
  if (sale.state !== 'voided' || hasVoidedEvent) return [];
  return [{
    key: 'voided-legacy',
    title: t('detail.activity.voided'),
    detail: sale.cancellation_reason || undefined,
    sortKey: '',
    displayDate: null,
    accent: 'var(--accent-cobre)',
  }];
}

/**
 * Collects the record's dated facts into one reverse-chronological list.
 *
 * A sale voided before the `events` field existed has no timestamp for it
 * on file — that entry is pinned first, undated, same as the record's own
 * gap. Once `events` carries the fact, it sorts in like everything else.
 *
 * @param sale - The sale record.
 * @param milestoneTypes - The agency's configured milestone types.
 * @param t - The namespace translator.
 * @returns The feed entries, newest first.
 */
function buildEntries(
  sale: Sale, milestoneTypes: SalePaperworkMilestoneType[], t: Translate,
): FeedEntry[] {
  const dated = [
    ...milestoneEntries(sale, milestoneTypes, t),
    ...receiptEntries(sale, t),
    ...stateEventEntries(sale, t),
  ].sort((a, b) => (b.sortKey ?? '').localeCompare(a.sortKey ?? ''));

  return [...legacyVoidEntries(sale, t), ...dated];
}

export default SaleActivityFeed;
